import { Box, CircularProgress, Dialog, Typography } from '@mui/material';

type LoaderVariant = 'fullpage' | 'inline' | 'dialog';

interface IProps {
  /**
   * `fullpage` (default): zentriert auf voller Viewport-Höhe — Hauptanwendung
   * für „App lädt"-Zustände.
   * `inline`: zentriert in einem `height`-bestimmten Container.
   * `dialog`: in einem MUI-Dialog (Modal-Overlay).
   */
  variant?: LoaderVariant;
  /** Optionaler Hinweistext unter dem Spinner. */
  label?: string;
  /** Höhe für die `inline`-Variante (default `40vh`). */
  height?: string | number;
  /** Größe des Spinners (CSS-Pixel oder MUI-Prop). */
  size?: number;
  /** Wird beim Schließen des Dialog-Overlays gefeuert (nur `dialog`). */
  onUnmount?: () => void;
}

/**
 * Zentraler Loader für die App. Drei Varianten decken die bestehenden
 * Verwendungsstellen ab; gibt es keinen `label`, wird kein Text angezeigt.
 */
export const Loader: React.FC<IProps> = ({
  variant = 'fullpage',
  label,
  height = '40vh',
  size,
  onUnmount,
}) => {
  const effectiveLabel =
    label !== undefined
      ? label
      : variant === 'fullpage'
      ? 'Portal wird geladen'
      : undefined;

  const content = (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      gap={effectiveLabel ? 2 : 0}
      height={
        variant === 'fullpage'
          ? '100vh'
          : variant === 'dialog'
          ? '50vh'
          : height
      }
    >
      <CircularProgress size={size} />
      {effectiveLabel && (
        <Typography variant='body2' color='text.secondary'>
          {effectiveLabel}
        </Typography>
      )}
    </Box>
  );

  if (variant === 'dialog') {
    return (
      <Dialog open onClose={onUnmount} maxWidth='sm' fullWidth scroll='paper'>
        {content}
      </Dialog>
    );
  }

  return content;
};
